import { useEffect, useRef } from 'react';

import { type NextRouter, useRouter } from 'next/router';

/** A soft exit the guard is holding until the agent confirms or declines it. */
export type SoftExit = {
  /** The same-origin path the agent was headed to. */
  destination: string;
  /**
   * Continues the exit the way it began. A link is pushed; Back pops the entry
   * the guard refused, so the wizard does not stay in history under the
   * destination where the next Back would reopen it. Resolves to whether the
   * route changed; when it did not, history is back where the wizard is, so
   * calling again retries the same exit.
   */
  follow: () => Promise<boolean>;
};

/**
 * The router handles a popstate on its own, with nothing to await, so the
 * outcome of a history move comes from its events.
 */
const nextRouteChange = (router: NextRouter): Promise<boolean> =>
  new Promise((resolve) => {
    const settle = (changed: boolean) => {
      router.events.off('routeChangeComplete', onComplete);
      router.events.off('routeChangeError', onError);
      resolve(changed);
    };
    const onComplete = () => settle(true);
    const onError = () => settle(false);
    router.events.on('routeChangeComplete', onComplete);
    router.events.on('routeChangeError', onError);
  });

const isModifiedClick = (event: MouseEvent): boolean =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

/** The address bar's path in the form Next reports as `as`. */
const currentPath = (): string =>
  window.location.pathname + window.location.search + window.location.hash;

/**
 * Finds the same-origin page link a click would follow, or null when the
 * browser should keep its default: modified clicks, new-tab and download
 * links, other origins (the beforeunload warning covers those), and hash
 * moves within the current page.
 */
const softExitTarget = (event: MouseEvent): string | null => {
  if (event.defaultPrevented || isModifiedClick(event)) return null;
  if (!(event.target instanceof Element)) return null;
  const anchor = event.target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.hasAttribute('download') || (anchor.target !== '' && anchor.target !== '_self')) {
    return null;
  }
  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  if (url.pathname + url.search === window.location.pathname + window.location.search) {
    return null;
  }
  return url.pathname + url.search + url.hash;
};

/**
 * Keeps an unfinished offer from being left by accident. Closing or reloading
 * the tab gets the browser's own warning. Soft exits, which never reach
 * beforeunload, go through `onSoftExit` instead: every same-origin link on the
 * page (header, footer, and the wizard's own links) and browser Back. The
 * caller decides whether to call the exit's `follow`.
 */
export const useOfferExitGuard = ({
  enabled,
  onSoftExit,
}: {
  enabled: boolean;
  onSoftExit: (exit: SoftExit) => void;
}): void => {
  const router = useRouter();
  const onSoftExitRef = useRef(onSoftExit);
  // Set while a confirmed Back is replaying, so the guard lets that one
  // popstate through even if the caller has not disabled it yet.
  const releasePop = useRef(false);
  useEffect(() => {
    onSoftExitRef.current = onSoftExit;
  }, [onSoftExit]);

  useEffect(() => {
    if (!enabled) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Browsers choose the warning text; returnValue supports older clients.
      // oxlint-disable-next-line typescript/no-deprecated -- The unload guard keeps the browser-compatible cancellation fallback
      event.returnValue = 'Your offer has not been submitted.';
    };
    // Capture phase runs before React's handlers. Preventing the default here
    // stops both the browser and next/link from navigating, while the link's
    // own click handlers (analytics) still run.
    const interceptLinkClick = (event: MouseEvent) => {
      const destination = softExitTarget(event);
      if (destination == null) return;
      event.preventDefault();
      onSoftExitRef.current({ destination, follow: () => router.push(destination) });
    };
    // Back has already moved the address bar when Next asks; re-push the
    // wizard's own entry so the URL matches the page that stays on screen.
    const wizardEntry: unknown = window.history.state;
    const wizardPath = router.asPath;
    const restoreWizardEntry = () => window.history.pushState(wizardEntry, '', wizardPath);
    const popBack = (destination: string): Promise<boolean> => {
      releasePop.current = true;
      const changed = nextRouteChange(router).then((routeChanged) => {
        releasePop.current = false;
        // The pop moved history before Next tried the route, so a failure
        // leaves the wizard on screen under the destination's URL. Put its
        // entry back so a retry pops to the same place; when history has
        // moved on since (another Back), that pop is no longer ours to undo.
        if (!routeChanged && currentPath() === destination) restoreWizardEntry();
        return routeChanged;
      });
      router.back();
      return changed;
    };
    router.beforePopState(({ as }) => {
      if (releasePop.current) return true;
      restoreWizardEntry();
      onSoftExitRef.current({ destination: as, follow: () => popBack(as) });
      return false;
    });
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', interceptLinkClick, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', interceptLinkClick, true);
      router.beforePopState(() => true);
    };
  }, [enabled, router]);
};
